import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { AssetMaintenance, MaintenanceParams, CreateMaintenanceDTO, Asset } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useAssetMaintenance(assetId?: string, initialParams?: MaintenanceParams) {
  const { inventoryRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<AssetMaintenance> | null>(null);
  const [maintenanceDueAssets, setMaintenanceDueAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<MaintenanceParams>(initialParams || { page: 1, limit: 20 });

  const fetchMaintenanceHistory = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryRepo.getMaintenanceHistory(assetId, params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar historial de mantenimiento');
    } finally {
      setLoading(false);
    }
  }, [inventoryRepo, assetId, params]);

  const fetchMaintenanceDueAssets = useCallback(async () => {
    try {
      const result = await inventoryRepo.getMaintenanceDueAssets();
      setMaintenanceDueAssets(result);
    } catch (err) {
      console.error('Error fetching maintenance due assets:', err);
    }
  }, [inventoryRepo]);

  useEffect(() => {
    if (assetId) {
      fetchMaintenanceHistory();
    }
  }, [fetchMaintenanceHistory, assetId]);

  useEffect(() => {
    fetchMaintenanceDueAssets();
  }, [fetchMaintenanceDueAssets]);

  const filterByType = useCallback((type: MaintenanceParams['type']) => {
    setParams(prev => ({ ...prev, type, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const createMaintenance = useCallback(async (targetAssetId: string, data: CreateMaintenanceDTO) => {
    const newMaintenance = await inventoryRepo.createMaintenance(targetAssetId, data);
    if (assetId === targetAssetId) {
      fetchMaintenanceHistory();
    }
    fetchMaintenanceDueAssets();
    return newMaintenance;
  }, [inventoryRepo, assetId, fetchMaintenanceHistory, fetchMaintenanceDueAssets]);

  return {
    maintenanceHistory: data?.data || [],
    pagination: data?.meta,
    maintenanceDueAssets,
    loading,
    error,
    params,
    filterByType,
    goToPage,
    createMaintenance,
    refresh: fetchMaintenanceHistory,
    refreshDueAssets: fetchMaintenanceDueAssets,
  };
}
